import React, { useState, useEffect } from 'react';
import { FileText, BookOpen, MessageCircle, Wand2, GraduationCap, BarChart3, Edit } from 'lucide-react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

import ChineseEssayWritingToolbox from './components/ChineseEssayWritingToolbox';
import DeepSeekChatWindow from './components/DeepSeekChatWindow';
import PrimaryChinese from './components/PrimaryChinese';
import KnowledgeGraph from './components/KnowledgeGraph';
import TextSizeControl from './components/TextSizeControl';
import A2AServerToggle from './components/A2AServerToggle';
import { TextSizeProvider, useTextSize } from './contexts/TextSizeContext';
import chineseDataService from './services/chineseDataService';

const AppContent = () => {
  const { textSize, setTextSize, tabLabelMode } = useTextSize();
  const [activeMainTab, setActiveMainTab] = useState('writing');
  const [activeSubTab, setActiveSubTab] = useState('knowledge-graph');
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [uxFormat, setUxFormat] = useState(null);

  // UX Negotiation - Detect context and negotiate UX format
  useEffect(() => {
    const initializeUX = async () => {
      try {
        // Detect user context
        const context = chineseDataService.detectContext();
        console.log('[PriChineseDaily] Detected context:', context);
        
        // Negotiate UX format via A2A
        const negotiatedUX = await chineseDataService.negotiateUX(context);
        console.log('[PriChineseDaily] Negotiated UX format:', negotiatedUX);
        
        setUxFormat(negotiatedUX);
        
        // Apply UX format settings
        if (negotiatedUX) {
          // Apply font size
          if (negotiatedUX.font_size) {
            const fontSizeMap = { large: 'lg', medium: 'md', small: 'sm' };
            setTextSize(fontSizeMap[negotiatedUX.font_size] || 'md');
          }
        }
      } catch (error) {
        console.error('[PriChineseDaily] Error during UX negotiation:', error);
        // Use default UX format on error
        const defaultContext = chineseDataService.detectContext();
        const defaultUX = chineseDataService.getDefaultUXFormat(defaultContext);
        setUxFormat(defaultUX);
      }
    };
    
    initializeUX();
  }, []);

  // Main tab configuration - Only keeping 2 tabs
  const mainTabs = [
    { id: 'writing', label: '中文寫作', icon: FileText },
    { id: 'primary', label: '小學中文', icon: GraduationCap }
  ];

  // Sub tab configuration for Chinese Writing - Only keeping 2 tabs
  const writingSubTabs = [
    { id: 'knowledge-graph', label: 'Knowledge Graph', icon: BarChart3 },
    { id: 'toolbox', label: '中文寫作工具箱', icon: Wand2 }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center">
                <Edit className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">中文作文助手</h1>
                <p className="text-sm text-gray-600">運用人工智能幫助創作優秀的中文作文</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Navigation Tabs */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex space-x-8">
            {mainTabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveMainTab(tab.id);
                    if (tab.id === 'writing') {
                      setActiveSubTab('knowledge-graph');
                    }
                  }}
                  className={`flex items-center ${tabLabelMode==='icon' ? '' : 'space-x-2'} py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeMainTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tabLabelMode==='icon_text' && <span>{tab.label}</span>}
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Sub Navigation Tabs - Only show for Chinese Writing */}
      {activeMainTab === 'writing' && (
        <nav className="bg-gray-50 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex space-x-6">
              {writingSubTabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveSubTab(tab.id)}
                    className={`flex items-center ${tabLabelMode==='icon' ? '' : 'space-x-2'} py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeSubTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tabLabelMode==='icon_text' && <span>{tab.label}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </nav>
      )}

      {/* Main Content */}
      <div 
        className={`max-w-7xl mx-auto px-4 py-6 ${
          uxFormat?.font_size === 'large' ? 'text-lg' : 
          uxFormat?.font_size === 'small' ? 'text-sm' : 
          'text-base'
        }`}
        style={{
          maxWidth: uxFormat?.layout === 'cards' ? '100%' : '80rem'
        }}
      >
        {/* Chinese Writing Tab Content */}
        {activeMainTab === 'writing' && (
          <>
            {/* Knowledge Graph Sub Tab */}
            {activeSubTab === 'knowledge-graph' && (
              <KnowledgeGraph uxFormat={uxFormat} />
            )}

            {/* Chinese Writing Toolbox Sub Tab */}
            {activeSubTab === 'toolbox' && (
              <ChineseEssayWritingToolbox uxFormat={uxFormat} />
            )}
          </>
        )}

        {/* Primary Chinese Tab Content */}
        {activeMainTab === 'primary' && (
          <PrimaryChinese uxFormat={uxFormat} />
        )}
      </div>

      {/* Floating Chat Button */}
      {!isChatOpen && (
        <button
          onClick={() => setIsChatOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center z-40 hover:scale-110"
          title="AI助手"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* DeepSeek Chat Window */}
      <DeepSeekChatWindow
        isOpen={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        contextData={null}
        initialMode="direct"
      />

      {/* Text Size Control */}
      <TextSizeControl 
        textSize={textSize} 
        onTextSizeChange={setTextSize} 
      />

      {/* A2A Server Toggle - Always visible for Cloudflare/localhost switching */}
      <A2AServerToggle />
    </div>
  );
};

const App = () => {
  return (
    <TextSizeProvider>
      <AppContent />
    </TextSizeProvider>
  );
};

export default App;